import { Injectable, Inject } from '@angular/core';
import { LOCAL_STORAGE, StorageService } from 'ngx-webstorage-service';
import { SelectionModel } from '@angular/cdk/collections';
import { lastValueFrom } from 'rxjs/internal/lastValueFrom';

@Injectable({ providedIn: 'root' })
export class TableService {
  public selectedRows: SelectionModel<any>;
  public allSelected = false;
  public dataLength = 0;
  public selectedData;

  // Query-time terms rebuilt before every search (getPageData) — persisting
  // them bloats each grid's localStorage entry by ~20KB for no benefit.
  private static readonly QUERY_TIME_TERMS = ['fields', 'fields_to_project', 'relatedModuleSearchFields'];

  constructor(
    @Inject(LOCAL_STORAGE) private storage: StorageService
  ) { }


  // Fresh object per call — a shared default instance would leak one grid's
  // options into another (this service is a root singleton used by every grid).
  private freshSavedOptions() {
    return {
      searchTerms: {},
      pageNumber: 0,
      pageSize: 10,
      tableColumns: []
    };
  }


  resetSelectedRows() {
    this.allSelected = false;
    this.selectedRows = new SelectionModel<any>(true, []);
  }


  async selectAll(_searchTerms, searchFunc) {
    this.allSelected = true;

    const _results: any[] = await lastValueFrom(searchFunc) || [];
    // console.log('_results: ', _results);

    _results.forEach(row => this.selectedRows.select(row))
  }


  setResults(_results) {
    if (!this.selectedRows) this.resetSelectedRows();

    // SelectionModel dedupes by object identity, so a re-fetched record (new object,
    // same _id) selects twice — that's how repeated Select All doubled campaign
    // recipient lists (case 1361). Dedupe by _id here; rows without an _id keep the
    // old identity behavior.
    if (_results && _results.length) {
      const selectedIds = new Set(this.selectedRows.selected.map(_row => _row?._id ? '' + _row._id : null).filter(Boolean));

      _results.forEach(row => {
        if (row?._id) {
          if (selectedIds.has('' + row._id)) return;
          selectedIds.add('' + row._id);
        }
        this.selectedRows.select(row);
      });
    }
  }


  deselectAll() {
    this.selectedRows.clear();
  }


  getSelectedItems() {
    return this.selectedRows.selected;
  }


  /** Selects all rows if they are not all selected; otherwise clear selectedRows. */
  masterToggle() {
    this.isAllSelected() ? this.selectedRows.clear() : this.selectedData.forEach(row => this.selectedRows.select(row));
  }


  /**
   * Determines whether a value is selected.
   */
  isItemSelected(itemId) {
    let found = false;

    if (this.selectedRows && this.selectedRows.selected) {
      for (let i = 0; i < this.selectedRows.selected.length; i++) {
        if (this.selectedRows.selected[i]._id === itemId) {
          found = true;
          break;
        }
      }
    }

    return found;
  }


  toggleItem(item) {
    if (!this.selectedRows) {
      console.log('Please set selected rows to something...');
      return;
    }

    const index = this.selectedRows.selected.indexOf(this.selectedRows.selected.find(s => s._id == item._id));

    if (index !== -1) {
      this.selectedRows.selected.splice(index, 1);
    } else {
      this.selectedRows.selected.push(item);
    }
  }


  // hasSubArray(master, sub) {
  //   let containsAllColumns = true;

  //   sub.forEach(col => {
  //     if (!master.includes(col)) {
  //       containsAllColumns = false;
  //       return containsAllColumns;
  //     }
  //   })

  //   return containsAllColumns;
  // }


  hasSubArray(allColumns, tableColumns) {
    let containsAllColumns = true;

    const allColsAsStrings: string[] = allColumns.map(col => col.field);

    tableColumns.forEach(col => {
      if (col !== 'select' && col !== 'view' && !allColsAsStrings.includes(col)) {
        containsAllColumns = false;
        return containsAllColumns;
      }
    })

    return containsAllColumns;
  }


  addMissingDisplayedColumns(displayedColumns, tableColumns) {
    const initDisplayedColumns = (displayedColumns && displayedColumns.length) ? [...displayedColumns] : [];

    for (let index = 0; index < initDisplayedColumns.length; index++) {
      const col = initDisplayedColumns[index];

      // If it is a field column and not the passed tableColumns add it at the correct index.
      if (col !== 'select' && col !== 'view' && !tableColumns.includes(col)) {
        tableColumns.splice(index, 0, col)
      }
    }

    return tableColumns;
  }


  hasSelectedItems() {
    return (this.selectedRows && this.selectedRows.selected) ? this.selectedRows.selected.length : 0;
  }


  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected() {
    return (this.selectedRows) ? this.selectedRows.selected.length === this.dataLength : false;
  }


  saveSearchTermsInLocal(key, val): void {
    try {
      const currentSavedOptions = this.getTableOptionsFromLocal(key) || this.freshSavedOptions();

      const termsToSave = { ...val };
      TableService.QUERY_TIME_TERMS.forEach(_t => delete termsToSave[_t]);

      currentSavedOptions.searchTerms = termsToSave;
      this.storage.set(key, JSON.stringify(currentSavedOptions));
    } catch (_err) {
      console.log('ERROR saving search terms in local storage: ');
      console.log("key: ", key);
      console.log("val: ", val);
    }
  }


  getSearchTermsFromLocal(key) {
    const currentSavedOptions = this.getTableOptionsFromLocal(key);
    return (currentSavedOptions && currentSavedOptions.searchTerms) ? currentSavedOptions.searchTerms : null;
  }


  saveTableOptionsInLocal(key, pageNumber, pageSize, displayedColumns = null) {
    const currentSavedOptions = this.getTableOptionsFromLocal(key) || this.freshSavedOptions();

    currentSavedOptions.pageNumber = pageNumber;
    currentSavedOptions.pageSize = pageSize;

    if (displayedColumns) {
      currentSavedOptions.tableColumns = displayedColumns;
    }

    this.storage.set(key, JSON.stringify(currentSavedOptions));
  }


  // Returns THIS key's saved options, or null if none stored. Never fall back
  // to another grid's options: the old shared-state fallback made grid B open
  // with grid A's filters whenever B had nothing saved (case 1322 — "Rejected
  // status randomly gets pulled in").
  getTableOptionsFromLocal(key) {
    try {
      const stored = this.storage.get(key);
      return stored ? JSON.parse(stored) : null;
    } catch (_err) {
      return null; // corrupt/legacy entry — treat as nothing saved
    }
  }


  toggleCustomColumn(_field, _displayedColumns, _allColumns) {
    // console.log('Field: ', _field);
    // console.log('_displayedColumns: ', _displayedColumns);
    // console.log('_allColumns: ', _allColumns);

    if (_displayedColumns.includes(_field)) {
      // want to hide this column. Remove it from displayed columns array
      _displayedColumns.splice(_displayedColumns.indexOf(_field), 1);
    } else {
      // not being shown, select it
      let insertAt = _allColumns.findIndex(_ac => _ac.fieldName === _field);

      if (insertAt >= _displayedColumns.length) {
        // always want the view column to be last
        insertAt = _displayedColumns.length - 1;
      }

      _displayedColumns.splice(insertAt, 0, _field);
    }
  }


  // Used for optional columns
  toggleColumn(_field, _displayedColumns, _allColumns) {
    // console.log('_displayedColumns: ', _displayedColumns);
    // console.log('_allColumns: ', _allColumns);

    if (_displayedColumns.includes(_field)) {
      // want to hide this column. Remove it from displayed columns array
      _displayedColumns.splice(_displayedColumns.indexOf(_field), 1);
    } else {
      // not being shown, select it
      let insertAt = _allColumns.indexOf(_field);

      if (insertAt >= _displayedColumns.length) {
        // always want the view column to be last
        insertAt = _displayedColumns.length - 1;
      }

      _displayedColumns.splice(insertAt, 0, _field);
    }
  }
}